import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  getInAppFirstUrlParam,
  getIsStickyInAppFirst
} from 'modules/search/selectors'
import {setIsStickyInAppFirst} from 'modules/search/slice'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

import {hasStickyInAppFirst, setStickyInAppFirst} from './sessionStore'

/**
 * Mounted once at the app shell. Responsible for two related effects:
 *
 *  1. **Bootstrap:** When the brand is eligible (`shell.supportsInAppFirst`) and
 *     the URL carries `inAppFirst=1` and the tab isn't already sticky, write the
 *     sticky flag to `sessionStorage` and promote the Redux flag.
 *
 *  2. **Recovery:** `sessionStorage` is the single source of truth, but SSR
 *     can't read it, so the preloaded Redux state always starts non-sticky.
 *     On a full reload of an already-in-app tab the URL param may be gone while
 *     `sessionStorage` still holds the flag — sync Redux up to it. There is no
 *     stale-cookie case to clear: a fresh tab simply has empty
 *     `sessionStorage`, so Redux and storage already agree (both false).
 */
export const useInAppFirstBootstrap = (): void => {
  const {
    shell: {supportsInAppFirst}
  } = useBrandCapabilities()
  const inAppFirstParam = useSelector(getInAppFirstUrlParam)
  const sticky = useSelector(getIsStickyInAppFirst)
  const dispatch = useDispatch()

  useEffect(() => {
    const sessionHasSticky = hasStickyInAppFirst()

    const hasParam = inAppFirstParam === '1' || inAppFirstParam === 1

    if (supportsInAppFirst && hasParam && !sessionHasSticky) {
      setStickyInAppFirst()
      dispatch(setIsStickyInAppFirst(true))
      return
    }

    if (sticky !== sessionHasSticky) {
      dispatch(setIsStickyInAppFirst(sessionHasSticky))
    }
  }, [supportsInAppFirst, inAppFirstParam, sticky, dispatch])
}
